import React from "react"
import { NavLink as BaseLink } from "react-router-dom"
import styled, { css } from "styled-components"
import { space, SpaceProps } from "styled-system"
import { typography, textTransform, textDecoration, FontConfigurations, UNDERLINE_TEXT_CSS } from "theme"

import { TextProps, Paragraph } from "components/text"
import Icon, { IconName } from "components/icon"

/* One-off placement of this SVG which needs inline box */
import ArrowRight from "assets/icons/arrow-right.svg"

export const linkCSS = css`
  text-decoration: none;
  :hover {
    cursor: pointer;
    opacity: ${props => props.theme.reducedOpacity};
  }

  ${typography}
  ${textTransform}
  ${textDecoration}
`

export const PlainLink = styled(({ children, lineHeight: _l, ...restProps }) => <a {...restProps}>{children}</a>)<{
  underline?: boolean
}>`
  ${linkCSS}
  ${({ underline = true }) => (underline ? UNDERLINE_TEXT_CSS : "")}
`

/*
  This is a workaround for a known issue when using styled-components with external components
  We need to "filter" the props manually
  Github issue for reference: https://github.com/styled-components/styled-components/issues/305
*/
export const Link = styled(
  ({ textTransform: _t, alignSelf: _a, iconPosition: _i, lineHeight: _l, children, ...restProps }) => (
    <BaseLink {...restProps}>{children}</BaseLink>
  )
)<{ underline?: boolean }>`
  ${linkCSS}
  ${props => (props.underline ? UNDERLINE_TEXT_CSS : "")}
`

type LinkProps = {
  to: string
  size?: "sm" | "md" | "lg"
  children: React.ReactNode
} & TextProps

export const TextLink = ({ size, to, children, ...props }: LinkProps) => {
  let fontConfig

  switch (size) {
    case "sm":
      fontConfig = FontConfigurations.pSm
      break
    case "lg":
      fontConfig = FontConfigurations.pLg
      break
    case "md":
    default:
      fontConfig = FontConfigurations.p
  }

  return (
    <Link to={to} {...fontConfig} {...props}>
      {children}
    </Link>
  )
}
TextLink.defaultProps = {
  textDecoration: "underline"
}

const AlignedLink = styled(Link)<SpaceProps & { iconPosition: "left" | "right" }>`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  flex-direction: ${props => (props.iconPosition === "left" ? "row" : "row-reverse")};
  ${space}
`

type LinkWithIconProps = {
  to: string
  iconName: IconName
  iconPosition: "left" | "right"
  children: React.ReactNode
} & TextProps

export function LinkWithIcon({ to, iconName, iconPosition, children, ...styledProps }: LinkWithIconProps) {
  const mx = { [iconPosition === "left" ? "mr" : "ml"]: 3 } /* Set left or right margin based on the icon placement. */

  return (
    <AlignedLink to={to} iconPosition={iconPosition} {...styledProps}>
      <Icon name={iconName} {...mx} />
      <Paragraph bold>{children}</Paragraph>
    </AlignedLink>
  )
}

const arrowLinkStyles = css`
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  font-weight: bold;
  line-height: 1.25;
  svg {
    margin-left: ${props => props.theme.space[2]};
    display: inline;
  }
  &:hover svg {
    transform: translateX(${props => props.theme.space[2]});
  }
`

const ArrowLink = styled(Link)`
  ${arrowLinkStyles}
`

const NativeArrowLink = styled(PlainLink)`
  ${arrowLinkStyles}
`

type ArrowLinkProps = {
  to?: string
  href?: string
  size?: "small" | "large"
  children: React.ReactNode
} & TextProps

export function LinkWithArrow({ to, href, size, children, ...styledProps }: ArrowLinkProps) {
  let fontConfig

  switch (size) {
    case "small":
      fontConfig = FontConfigurations.pSm
      break
    case "large":
      fontConfig = FontConfigurations.pLg
      break
    default:
      fontConfig = FontConfigurations.p
  }

  if (to) {
    return (
      <ArrowLink to={to} {...fontConfig} {...styledProps}>
        {children}
        <ArrowRight />
      </ArrowLink>
    )
  }

  const { lineHeight: _l, ...fontConfigWithoutLineHeight } = fontConfig

  return (
    <NativeArrowLink href={href} {...fontConfigWithoutLineHeight} {...styledProps}>
      {children}
      <ArrowRight />
    </NativeArrowLink>
  )
}
